import { cva, RecipeVariantProps } from "styled-system/css";

export type FormControlVariants = NonNullable<RecipeVariantProps<typeof formControl>>;

export const formControl = cva({
  base: {
    display: "flex",
    alignItems: "center",
    w: "full",
    gap: "8",
    rounded: "300",
    borderStyle: "solid",
    borderWidth: "100",
    whiteSpace: "nowrap",

    // Apply the following styles to the current element and any child input or textarea elements
    "&, & input, & textarea": {
      bg: "var(--bg-fill)",
      borderColor: "border.secondary",
      color: "text.secondary",
      transitionPreset: "common",
      "&::placeholder, &[data-placeholder], &[data-placeholder-shown]": {
        color: { base: "text.tertiary", _disabled: "text.disabled" },
      },
      _focus: {
        outline: "[none]",
      },
    },
    "&:hover:not(:disabled)": {
      "&, & input, & textarea": {
        bg: "var(--bg-fill)",
        borderColor: "border.tertiary",
      },
    },
    "&:is(:focus-visible, [data-active]), &:has(input:focus-visible), &:has(textarea:focus-visible)":
      {
        "&, & input, & textarea": {
          bg: "var(--bg-fill)",
          borderColor: "border.tertiary",
        },
      },
    "&:disabled, &:has(input:disabled), & input:disabled, &:has(textarea:disabled), & textarea:disabled":
      {
        bg: "var(--bg-fill)",
        color: "text.disabled",
        cursor: "not-allowed",
        borderColor: "border.primary",
      },
  },
  variants: {
    size: {
      sm: {
        px: {
          base: "[calc({spacing.8} - {borderWidths.100})]",
          desktop: "[calc({spacing.8} - {borderWidths.100})]",
        },
        py: {
          base: "[calc(6px - {borderWidths.100})]",
          desktop: "[calc({spacing.8} - {borderWidths.100})]",
        },
        "&, & input, & textarea": { textStyle: { base: "body.lg", desktop: "body.sm" } },
      },
      md: {
        px: {
          base: "[calc({spacing.8} - {borderWidths.100})]",
          desktop: "[calc({spacing.12} - {borderWidths.100})]",
        },
        py: {
          base: "[calc({spacing.12} - {borderWidths.100})]",
          desktop: "[calc({spacing.8} - {borderWidths.100})]",
        },
        "&, & input, & textarea": { textStyle: { base: "body.lg", desktop: "body.md" } },
      },
      lg: {
        px: {
          base: "[calc({spacing.12} - {borderWidths.100})]",
          desktop: "[calc({spacing.12} - {borderWidths.100})]",
        },
        py: {
          base: "[calc({spacing.12} - {borderWidths.100})]",
          desktop: "[calc(10px - {borderWidths.100})]",
        },
        "&, & input, & textarea": { textStyle: { base: "body.xl", desktop: "body.lg" } },
      },
    },

    state: {
      default: {},
      loading: {
        opacity: "0.7",
        transition: "opacity",
      },
      error: {
        borderColor: "border.negative!",
      },
      warning: {
        borderColor: "border.negative!",
      },
      success: {
        borderColor: "border.positive!",
      },
    },

    variant: {
      default: { "--bg-fill": "{colors.background.secondary}" },
      transparent: { "--bg-fill": "transparent" },
    },
  },
  defaultVariants: { size: "md", state: "default", variant: "default" },
});
